import React, {FC, useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {PageTitle} from '../../../_metronic/layout/core'
import {ChannelIdentity} from './core/_models'
import {getChannelIdentities, requestErrorMessage, revokeChannelIdentity} from './core/_requests'
import './Channels.css'

const PROVIDER_LABEL: Record<string, string> = {discord: 'Discord', slack: 'Slack', teams: 'Teams'}

const formatDate = (iso: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR')
}

const MyChannelsPage: FC = () => {
  const [items, setItems] = useState<ChannelIdentity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [toRemove, setToRemove] = useState<ChannelIdentity | null>(null)
  const [removing, setRemoving] = useState(false)

  const load = () => {
    setLoading(true)
    getChannelIdentities()
      .then((data) => {
        setItems(data)
        setError('')
      })
      .catch((e) => setError(requestErrorMessage(e, 'Não foi possível carregar seus canais.')))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const remove = () => {
    if (!toRemove) return
    setRemoving(true)
    revokeChannelIdentity(toRemove.id)
      .then(() => {
        setToRemove(null)
        load()
      })
      .catch((e) => {
        setError(requestErrorMessage(e, 'Não foi possível desconectar agora.'))
        setToRemove(null)
      })
      .finally(() => setRemoving(false))
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Meus canais</PageTitle>

      <div className='dash-section'>
        <div className='mb-5'>
          <h3 className='dash-section-title mb-1'>Canais conectados</h3>
          <span className='text-muted fw-semibold fs-7'>
            Contas em que você conversa com o RushMind. Desconectar interrompe o acesso na hora.
          </span>
        </div>

        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
          </div>
        )}

        {loading && <p className='text-muted mb-0'>Carregando...</p>}

        {!loading && !error && items.length === 0 && (
          <div className='text-center py-8' data-testid='channels-empty'>
            <i className='bi bi-chat-dots fs-1 text-muted d-block mb-3' aria-hidden='true' />
            <p className='fw-semibold mb-1'>Você ainda não conectou nenhum canal.</p>
            <p className='text-muted mb-0'>
              No Discord, mande uma mensagem direta para o RushMind e siga o link que ele enviar.
            </p>
          </div>
        )}

        {!loading &&
          items.map((item) => (
            <div className='channels-list-item' key={item.id} data-testid='channel-item'>
              <div className='d-flex align-items-center gap-3'>
                <i className='bi bi-discord channels-provider-icon' aria-hidden='true' />
                <div>
                  <div className='fw-bold'>
                    {PROVIDER_LABEL[item.provider] || item.provider}: {item.display_name}
                  </div>
                  {item.linked_at && (
                    <div className='text-muted fs-7'>Conectado em {formatDate(item.linked_at)}</div>
                  )}
                </div>
              </div>
              <button type='button' className='btn btn-light' onClick={() => setToRemove(item)}>
                Desconectar
              </button>
            </div>
          ))}
      </div>

      <Modal show={toRemove !== null} onHide={() => setToRemove(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Desconectar conta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Ao desconectar <strong>{toRemove?.display_name}</strong>, o RushMind deixa de responder a essa
          conta. Você pode conectar de novo quando quiser.
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setToRemove(null)} disabled={removing}>
            Cancelar
          </Button>
          <Button variant='primary' onClick={remove} disabled={removing}>
            {removing ? 'Desconectando...' : 'Desconectar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {MyChannelsPage}
